import {
  BusinessOnlinePricing,
  OnlineBucketField,
  OnlineBucketGroupCoverageEntry,
  OnlineBucketGroupCoverageMap,
  OnlineCardSplitTemplate,
  OnlineGroupKey,
  OnlinePricingStructure,
} from "../../../interfaces/onlinePricing.interface";
import { IBusinessFull } from "../../../interfaces/businessFull.interface";
import {
  OnlinePricingTemplate,
  PartnerBucketGroupPrice,
  ValueType,
} from "../../../interfaces/partnerDeal.interface";
import { isMerchantSubmittedToAdyen, isNoEdit } from "./isNoEdit";
import { KlearlyCountryAlpha2Code } from "../../../interfaces/klearlyCountryAlpha2Code.enum";

// ---------------------------------------------------------------------------
// The 16-bucket online pricing model.
//
// A 16-bucket merchant is priced on a small set of GROUPS; each group's rate
// applies to every BUCKET it covers. Which groups exist is the product of two
// template axes stored on the business: `onlineCardSplitTemplate` (which card
// bucket is priced on its own) and `onlinePricingTemplate` (which local payment
// method is priced, if any).
//
// At runtime the group → bucket mapping comes from the read's
// `onlineBucketGroupCoverage`. When that is missing, stored group keys are
// shown ungrouped rather than guessed from a client-side table.
//
// Mapped merchants: Partner Mapper follows the partner preview's discriminator.
// Pricing (New) keeps an unlabelled snapshot on the Online buckets pane.
// ---------------------------------------------------------------------------

/** Fields the structure is read off. A merchant that has never been priced has
 *  no discriminator to read, so the rest of it decides. */
export type OnlinePricingStructureSource = Pick<
  IBusinessFull,
  | "onlinePricingStructure"
  | "onlinePricing"
  | "partnerId"
  | "noEdit"
  | "isSubmittedToAdyen"
> &
  Partial<Pick<IBusinessFull, "onlineMerchantServiceChargeFees">> & {
    adyen?: { splitConfigurationId?: string };
  };

/** Whether the merchant carries an online rate today. A group stored at 0 counts:
 *  the merchant was priced, badly, and repricing it is a four-bucket edit — not
 *  the untouched merchant the 16-bucket default is for. */
const hasStoredOnlinePricing = (
  business: OnlinePricingStructureSource | null | undefined
): boolean =>
  Object.values(business?.onlinePricing ?? {}).some(
    (group) => typeof group?.value === "number"
  );

/** PC-1010: a merchant that has never been priced is priced on sixteen buckets.
 *  Only one that has nothing to lose: no partner to inherit from, no stored rate
 *  to reinterpret, and not yet submitted to Adyen — a submitted merchant's
 *  pricing has to keep describing what Adyen holds.
 *
 *  `noEdit` is the submission signal rather than `adyen.onboardingStatus`, which
 *  the read enriches with a truthy value ("onboardingAllowed") for merchants
 *  that have not been submitted at all. The backend fills `noEdit` only once a
 *  merchant is submitted, and freezes the derived bucket rows in it either way. */
const isUnpricedDirectMerchant = (
  business: OnlinePricingStructureSource | null | undefined
): boolean =>
  !!business &&
  !business.partnerId &&
  !hasStoredOnlinePricing(business) &&
  !isNoEdit(business.noEdit, "onlineMerchantServiceChargeFees");

/** Which shape a merchant is priced on. The stored discriminator wins wherever
 *  there is one — a migrated merchant's key set proves nothing (see the inert
 *  groups note on buildSixteenBucketOnlinePricing), and one already labelled
 *  four-bucket stays there. Only a merchant that has never been priced has no
 *  label, and it is priced on the current structure. */
export function resolveOnlinePricingStructure(
  business: OnlinePricingStructureSource | null | undefined
): OnlinePricingStructure {
  // Any stored label, including one KBM does not recognise, is the merchant's
  // own answer and outranks the default.
  if (business?.onlinePricingStructure) {
    return business.onlinePricingStructure ===
      OnlinePricingStructure.SixteenBucket
      ? OnlinePricingStructure.SixteenBucket
      : OnlinePricingStructure.FourBucket;
  }
  return isUnpricedDirectMerchant(business)
    ? OnlinePricingStructure.SixteenBucket
    : OnlinePricingStructure.FourBucket;
}

export function isSixteenBucketMerchant(
  business: OnlinePricingStructureSource | null | undefined
): boolean {
  return (
    resolveOnlinePricingStructure(business) ===
    OnlinePricingStructure.SixteenBucket
  );
}

/** Pricing (New) and its editor prefetch use this same gate for the online preview. */
export function shouldLoadOnlinePricingPreview(params: {
  business: OnlinePricingStructureSource | null | undefined;
  partnerId?: string;
}): boolean {
  return (
    isSixteenBucketMerchant(params.business) ||
    (!isMerchantSubmittedToAdyen(params.business) && !!params.partnerId)
  );
}

export const isKnownOnlinePricingStructure = (
  structure: string | undefined
): structure is OnlinePricingStructure =>
  structure === OnlinePricingStructure.SixteenBucket ||
  structure === OnlinePricingStructure.FourBucket;

/** Whether a 16-bucket snapshot has anything to lose by moving to four buckets:
 *  a group or a bucket row priced above zero, or a merchant already submitted to
 *  Adyen. Mirrors the backend's downgrade guard, which lets four buckets replace
 *  only an unpriced snapshot of an unsubmitted merchant. Submission is read the
 *  KBM way (see isMerchantSubmittedToAdyen): the read fills
 *  `adyen.onboardingStatus` for merchants that were never submitted. */
export function sixteenBucketSnapshotIsKept(
  business: OnlinePricingStructureSource | null | undefined
): boolean {
  const priced = (value: unknown) => typeof value === "number" && value > 0;
  return (
    isMerchantSubmittedToAdyen(business) ||
    Object.values(business?.onlinePricing ?? {}).some((group) =>
      priced(group?.value)
    ) ||
    (business?.onlineMerchantServiceChargeFees ?? []).some((fee) =>
      priced(fee.value)
    )
  );
}

/** Pane renderer. Partner Mapper follows the preview; Pricing (New) keeps
 *  unlabelled merchants on the Online buckets pane. A four-bucket merchant
 *  not live on Adyen follows an explicit 16-bucket range pick. A labelled
 *  16-bucket merchant follows a four-bucket preview only while it has nothing
 *  to lose — see sixteenBucketSnapshotIsKept. */
export function resolveEditorOnlinePricingStructure(params: {
  business: OnlinePricingStructureSource | null | undefined;
  previewStructure?: string;
  mappedPartner?: boolean;
  followPartnerPreview?: boolean;
  selectedProfileIsSixteenBucket?: boolean;
  profilePickPending?: boolean;
}): OnlinePricingStructure {
  if (params.followPartnerPreview === false) {
    const labelledSixteen =
      params.business?.onlinePricingStructure ===
      OnlinePricingStructure.SixteenBucket;
    if (labelledSixteen) {
      // The backend refuses four buckets over priced or submitted 16-bucket
      // rates, so a four-bucket range does not move this merchant's pane.
      if (sixteenBucketSnapshotIsKept(params.business)) {
        return OnlinePricingStructure.SixteenBucket;
      }
      if (isKnownOnlinePricingStructure(params.previewStructure)) {
        return params.previewStructure;
      }
      return OnlinePricingStructure.SixteenBucket;
    }
    if (
      params.business?.onlinePricingStructure ===
        OnlinePricingStructure.FourBucket &&
      !isMerchantSubmittedToAdyen(params.business) &&
      (params.selectedProfileIsSixteenBucket || params.profilePickPending)
    ) {
      return OnlinePricingStructure.SixteenBucket;
    }
    const mapped = params.mappedPartner ?? !!params.business?.partnerId;
    if (mapped) return OnlinePricingStructure.FourBucket;
    return resolveOnlinePricingStructure(params.business);
  }
  if (isKnownOnlinePricingStructure(params.previewStructure)) {
    return params.previewStructure;
  }
  const mapped = params.mappedPartner ?? !!params.business?.partnerId;
  if (
    mapped &&
    !params.business?.onlinePricingStructure &&
    !hasStoredOnlinePricing(params.business)
  ) {
    return OnlinePricingStructure.SixteenBucket;
  }
  return resolveOnlinePricingStructure(params.business);
}

export function onlineCardSplitHeading(template?: string): string {
  if (template === OnlineCardSplitTemplate.EuropeanDebitSplit) {
    return "EEA Debit Split";
  }
  if (template === OnlineCardSplitTemplate.DomesticCreditSplit) {
    return "Domestic Credit Split";
  }
  return "Default – Interchange fee based";
}

// KBM owns every display string: the backend deliberately sends bucket field
// identifiers, not labels.
export const ONLINE_BUCKET_FIELD_LABELS: Record<OnlineBucketField, string> = {
  [OnlineBucketField.DomesticDebit]: "Domestic Debit",
  [OnlineBucketField.DomesticCredit]: "Domestic Credit",
  [OnlineBucketField.EeaDebit]: "E.E.A Debit",
  [OnlineBucketField.EuropeanCredit]: "European Credit",
  [OnlineBucketField.DomesticCorporateDebit]: "Domestic Corporate Debit",
  [OnlineBucketField.DomesticCorporateCredit]: "Domestic Corporate Credit",
  [OnlineBucketField.EuropeanCorporateDebit]: "European Corporate Debit",
  [OnlineBucketField.EuropeanCorporateCredit]: "European Corporate Credit",
  [OnlineBucketField.InternationalDebit]: "International Debit",
  [OnlineBucketField.InternationalCredit]: "International Credit",
  [OnlineBucketField.InternationalCorporateDebit]:
    "International Corporate Debit",
  [OnlineBucketField.InternationalCorporateCredit]:
    "International Corporate Credit",
  [OnlineBucketField.Amex]: "AMEX",
  [OnlineBucketField.AppleGooglePay]: "Apple Pay / Google Pay",
  [OnlineBucketField.IDeal]: "iDEAL",
  [OnlineBucketField.Bancontact]: "Bancontact",
};

/** Label for a bucket field identifier, falling back to the raw identifier so
 *  a bucket the backend adds before KBM knows about it is still readable. */
export const onlineBucketFieldLabel = (name: string): string =>
  ONLINE_BUCKET_FIELD_LABELS[name as OnlineBucketField] ?? name;

/** Display order for bucket rows, mirroring the backend's
 *  ONLINE_SIXTEEN_BUCKET_FIELDS. The in-person pane lists the same card buckets
 *  in this order, and an ungrouped online list has to agree with it. */
export const ONLINE_BUCKET_FIELD_ORDER: OnlineBucketField[] = [
  OnlineBucketField.DomesticDebit,
  OnlineBucketField.DomesticCredit,
  OnlineBucketField.DomesticCorporateDebit,
  OnlineBucketField.DomesticCorporateCredit,
  OnlineBucketField.EeaDebit,
  OnlineBucketField.EuropeanCredit,
  OnlineBucketField.EuropeanCorporateCredit,
  OnlineBucketField.EuropeanCorporateDebit,
  OnlineBucketField.InternationalDebit,
  OnlineBucketField.InternationalCredit,
  OnlineBucketField.InternationalCorporateDebit,
  OnlineBucketField.InternationalCorporateCredit,
  OnlineBucketField.Amex,
  OnlineBucketField.AppleGooglePay,
  OnlineBucketField.IDeal,
  OnlineBucketField.Bancontact,
];

/** Buckets in display order. A bucket this order does not name keeps its
 *  incoming position, after the ones it does — the same way an unknown group is
 *  kept rather than dropped. */
export function orderOnlineBuckets<T extends { field: string }>(
  buckets: T[]
): T[] {
  return [
    ...ONLINE_BUCKET_FIELD_ORDER.flatMap((field) =>
      buckets.filter((bucket) => bucket.field === field)
    ),
    ...buckets.filter(
      (bucket) =>
        !ONLINE_BUCKET_FIELD_ORDER.includes(bucket.field as OnlineBucketField)
    ),
  ];
}

export const ONLINE_GROUP_LABELS: Record<OnlineGroupKey, string> = {
  domesticDebitCards: "Domestic Debit",
  domesticCreditCards: "Domestic Credit",
  europeanDebitCards: "E.E.A Debit",
  european: "E.E.A",
  internationalAndCommercial: "Non E.E.A & Corporate",
  amex: "AMEX",
  appleGooglePay: "Apple Pay / Google Pay",
  iDeal: "iDEAL",
  bancontact: "Bancontact",
  corporateCreditCards: "Corporate Credit Cards",
  nonCorporateCreditCards: "Non-Corporate Credit Cards",
};

export const onlineGroupLabel = (key: string): string =>
  ONLINE_GROUP_LABELS[key as OnlineGroupKey] ?? key;

/** Display order for group sections. Cards first (narrowest to broadest), then
 *  AMEX, the wallet, and the local payment method last. */
export const ONLINE_GROUP_ORDER: OnlineGroupKey[] = [
  "domesticDebitCards",
  "domesticCreditCards",
  "europeanDebitCards",
  "european",
  "internationalAndCommercial",
  "amex",
  "appleGooglePay",
  "iDeal",
  "bancontact",
];

// The unit each group starts in before anything is stored for it. `bancontact` has
// no obvious one, so it has no entry and each caller supplies its own fallback.
const DEFAULT_UNIT_GROUPS: Partial<Record<OnlineGroupKey, ValueType>> = {
  domesticDebitCards: "Percentage",
  domesticCreditCards: "Percentage",
  europeanDebitCards: "Percentage",
  european: "Percentage",
  internationalAndCommercial: "Percentage",
  amex: "Percentage",
  appleGooglePay: "Percentage",
  iDeal: "Fixed",
  corporateCreditCards: "Percentage",
  nonCorporateCreditCards: "Percentage",
};

/** The unit a group starts in when nothing is stored for it. A group may be priced
 *  in either unit, so a stored or banded unit always takes precedence over this. */
export const onlineGroupValueType = (
  key: OnlineGroupKey
): ValueType | undefined => DEFAULT_UNIT_GROUPS[key];

/** The local payment method a country carries: NL iDEAL, BE Bancontact, nowhere
 *  else one at all. Mirrors the backend's onlinePricingTemplateForCountry, which
 *  is what a merchant write derives its own template from — a save offering the
 *  method the merchant's country does not carry is refused. */
const ONLINE_PRICING_TEMPLATE_BY_COUNTRY: Partial<
  Record<string, OnlinePricingTemplate>
> = {
  [KlearlyCountryAlpha2Code.Netherlands]: OnlinePricingTemplate.Default,
  [KlearlyCountryAlpha2Code.Belgium]: OnlinePricingTemplate.BancontactBE,
};

export function onlinePricingTemplateForCountry(
  country: string | null | undefined
): OnlinePricingTemplate {
  const normalized = country?.trim().toLowerCase();
  if (!normalized) return OnlinePricingTemplate.Default;
  return (
    ONLINE_PRICING_TEMPLATE_BY_COUNTRY[normalized] ??
    OnlinePricingTemplate.CardsOnly
  );
}

// Mirrors LemonPayBackend `onlineFeeBucketGroups.ts` entry for entry. Nothing
// here detects that file drifting; keep them in lockstep. Partner surfaces
// then append Apple Pay — see getOnlineSixteenBucketGroupKeys.
const ONLINE_CARD_BUCKET_FIELDS: readonly OnlineBucketField[] = [
  OnlineBucketField.DomesticDebit,
  OnlineBucketField.DomesticCredit,
  OnlineBucketField.DomesticCorporateDebit,
  OnlineBucketField.DomesticCorporateCredit,
  OnlineBucketField.EeaDebit,
  OnlineBucketField.EuropeanCredit,
  OnlineBucketField.EuropeanCorporateCredit,
  OnlineBucketField.EuropeanCorporateDebit,
  OnlineBucketField.InternationalDebit,
  OnlineBucketField.InternationalCredit,
  OnlineBucketField.InternationalCorporateDebit,
  OnlineBucketField.InternationalCorporateCredit,
];

const LOCAL_METHOD_BUCKET_FIELD: Record<
  OnlinePricingTemplate,
  OnlineBucketField | undefined
> = {
  [OnlinePricingTemplate.Default]: OnlineBucketField.IDeal,
  [OnlinePricingTemplate.BancontactBE]: OnlineBucketField.Bancontact,
  [OnlinePricingTemplate.CardsOnly]: undefined,
};

const BUCKET_GROUP_BY_BUCKET_FIELD: Partial<
  Record<OnlineBucketField, OnlineGroupKey>
> = {
  [OnlineBucketField.DomesticDebit]: "domesticDebitCards",
  [OnlineBucketField.EuropeanCredit]: "european",
  [OnlineBucketField.DomesticCorporateDebit]: "internationalAndCommercial",
  [OnlineBucketField.DomesticCorporateCredit]: "internationalAndCommercial",
  [OnlineBucketField.EuropeanCorporateCredit]: "internationalAndCommercial",
  [OnlineBucketField.EuropeanCorporateDebit]: "internationalAndCommercial",
  [OnlineBucketField.InternationalDebit]: "internationalAndCommercial",
  [OnlineBucketField.InternationalCredit]: "internationalAndCommercial",
  [OnlineBucketField.InternationalCorporateDebit]: "internationalAndCommercial",
  [OnlineBucketField.InternationalCorporateCredit]:
    "internationalAndCommercial",
  [OnlineBucketField.Amex]: "amex",
  [OnlineBucketField.AppleGooglePay]: "appleGooglePay",
  [OnlineBucketField.IDeal]: "iDeal",
  [OnlineBucketField.Bancontact]: "bancontact",
};

const SPLIT_BUCKET_GROUP_BY_BUCKET_FIELD: Partial<
  Record<
    OnlineBucketField,
    { template: OnlineCardSplitTemplate; group: OnlineGroupKey }
  >
> = {
  [OnlineBucketField.DomesticCredit]: {
    template: OnlineCardSplitTemplate.DomesticCreditSplit,
    group: "domesticCreditCards",
  },
  [OnlineBucketField.EeaDebit]: {
    template: OnlineCardSplitTemplate.EuropeanDebitSplit,
    group: "europeanDebitCards",
  },
};

/** Group a 16-bucket field is priced from under this card split. */
export function onlineBucketGroupForBucketField(
  bucketField: string,
  cardSplit: string
): OnlineGroupKey | undefined {
  const split =
    SPLIT_BUCKET_GROUP_BY_BUCKET_FIELD[bucketField as OnlineBucketField];
  if (split) {
    return cardSplit === split.template ? split.group : "european";
  }
  return BUCKET_GROUP_BY_BUCKET_FIELD[bucketField as OnlineBucketField];
}

const onlineBucketFieldsForTemplate = (
  onlinePricingTemplate: OnlinePricingTemplate
): OnlineBucketField[] => {
  const localMethodField = LOCAL_METHOD_BUCKET_FIELD[onlinePricingTemplate];
  return [
    ...ONLINE_CARD_BUCKET_FIELDS,
    OnlineBucketField.Amex,
    ...(localMethodField ? [localMethodField] : []),
  ];
};

/** Group keys a 16-bucket template pair prices, without the wallet group. */
export function onlineBucketGroupKeysForTemplates(
  cardSplit: string,
  onlinePricingTemplate: OnlinePricingTemplate
): OnlineGroupKey[] {
  const groups: OnlineGroupKey[] = [];
  for (const bucketField of onlineBucketFieldsForTemplate(
    onlinePricingTemplate
  )) {
    const group = onlineBucketGroupForBucketField(bucketField, cardSplit);
    if (group && !groups.includes(group)) groups.push(group);
  }
  return groups;
}

export const CANNOT_GROUP_PRICING_NOTE = "This pricing cannot be grouped";

const FOUR_BUCKET_ONLY_KEYS: ReadonlySet<string> = new Set([
  "corporateCreditCards",
  "nonCorporateCreditCards",
]);

export function orderOnlineGroupKeys(keys: string[]): OnlineGroupKey[] {
  const unique = Array.from(new Set(keys)) as OnlineGroupKey[];
  return [
    ...ONLINE_GROUP_ORDER.filter((key) => unique.includes(key)),
    ...unique.filter((key) => !ONLINE_GROUP_ORDER.includes(key)),
  ];
}

export function storedOnlineGroupKeys(
  groups?: BusinessOnlinePricing | null
): OnlineGroupKey[] {
  return orderOnlineGroupKeys(
    Object.keys(groups ?? {}).filter(
      (key) =>
        !FOUR_BUCKET_ONLY_KEYS.has(key) &&
        typeof groups?.[key as OnlineGroupKey]?.value === "number"
    )
  );
}

export type OnlinePricingGroup = {
  key: OnlineGroupKey;
  label: string;
  buckets: { field: string; label: string }[];
};

/** The preview's coverage entries as a group → buckets map. */
const coverageFromEntries = (
  entries: OnlineBucketGroupCoverageEntry[]
): OnlineBucketGroupCoverageMap =>
  entries.reduce<OnlineBucketGroupCoverageMap>((map, entry) => {
    if (entry?.group) map[entry.group] = entry.buckets ?? [];
    return map;
  }, {});

/** True when coverage names at least one bucket — empty shells are not a grouping. */
export const coverageHasBuckets = (
  coverage?: OnlineBucketGroupCoverageEntry[]
): boolean => !!coverage?.some((entry) => (entry.buckets?.length ?? 0) > 0);

const coverageFingerprint = (
  coverage?: OnlineBucketGroupCoverageEntry[]
): string | undefined => {
  if (!coverage || !coverageHasBuckets(coverage)) return undefined;
  return coverage
    .filter((entry) => (entry.buckets?.length ?? 0) > 0)
    .map(
      (entry) => `${entry.group}:${[...(entry.buckets ?? [])].sort().join(",")}`
    )
    .sort()
    .join("|");
};

/** Preview grouping when it matches the selected range (or none is selected);
 *  otherwise the range's own coverage. Leftover 4-bucket preview buckets are
 *  not a grouping. */
export function resolveOnlineGroupCoverage(
  previewCoverage?: OnlineBucketGroupCoverageEntry[],
  profileCoverage?: OnlineBucketGroupCoverageEntry[]
): OnlineBucketGroupCoverageEntry[] | undefined {
  if (coverageHasBuckets(previewCoverage)) {
    const profileKey = coverageFingerprint(profileCoverage);
    if (!profileKey || coverageFingerprint(previewCoverage) === profileKey) {
      return previewCoverage;
    }
  }
  if (coverageHasBuckets(profileCoverage)) return profileCoverage;
  return previewCoverage;
}

/** The groups a 16-bucket merchant is priced on, in display order, each with
 *  the buckets it covers. Coverage from the read wins; otherwise stored keys
 *  are shown with no buckets rather than a guessed grouping. */
export function sixteenBucketGroups(
  business: Pick<IBusinessFull, "onlinePricing"> | null | undefined,
  coverage?: OnlineBucketGroupCoverageEntry[]
): OnlinePricingGroup[] {
  const resolved = coverage?.length
    ? coverageFromEntries(coverage)
    : Object.fromEntries(
        storedOnlineGroupKeys(business?.onlinePricing).map((key) => [key, []])
      );
  return orderOnlineGroupKeys(Object.keys(resolved)).map((key) => ({
    key,
    label: onlineGroupLabel(key),
    buckets: (resolved[key] ?? []).map((field) => ({
      field,
      label: onlineBucketFieldLabel(field),
    })),
  }));
}

/** The rate a group carries today, or undefined when it has none. */
export const storedGroupPrice = (
  onlinePricing: BusinessOnlinePricing | undefined,
  key: OnlineGroupKey
): PartnerBucketGroupPrice | undefined => onlinePricing?.[key];

export type OnlineGroupDraft = { value: number | null; valueType: ValueType };

/** The `onlinePricing` a 16-bucket save may carry: the groups the merchant's
 *  template pair prices that the draft has a rate for.
 *
 *  Built from the group set rather than from what is stored, because migrated
 *  merchants keep inert groups from the other card splits (a default-split
 *  merchant still holds `domesticCreditCards` and `europeanDebitCards`), and
 *  echoing those back fails as "carries groups its card split does not price".
 *  A group the draft has no rate for is omitted rather than sent as 0, which the
 *  write path would read as a real rate; it is completed there from the
 *  per-bucket rows instead. Each group's stored entry is still the base, so the
 *  partner band snapshot the merchant was mapped under survives a save that only
 *  moves the rate. */
export function buildSixteenBucketOnlinePricing(
  groups: OnlinePricingGroup[],
  draft: Partial<Record<OnlineGroupKey, OnlineGroupDraft>>,
  stored: BusinessOnlinePricing | undefined
): BusinessOnlinePricing {
  const payload: BusinessOnlinePricing = {};
  groups.forEach(({ key }) => {
    const edited = draft[key];
    if (typeof edited?.value !== "number") return;
    const storedGroup = stored?.[key];
    payload[key] = {
      ...storedGroup,
      value: edited.value,
      valueType:
        edited.valueType ??
        storedGroup?.valueType ??
        onlineGroupValueType(key) ??
        "Percentage",
    };
  });
  return payload;
}
